// #MODULE - QuickBag
//
// > Author: Sarfaraz Merchant
// >
// > Create Date: May 3, 2016
// >
// ##DESCRIPTION: QuickBag pointing to REST API. It listens to few events. Written as part of NavApp stability project (bag componentization)

import $ from 'jquery';
import _ from 'underscore';
import PageApp from 'pageApp';
import Globals from '@component/common/src/util/Globals';
import TagManagerUtil from '@component/common/src/util/TagManagerUtil';
import ObjectUtil from '@component/common/src/util/ObjectUtil';
import MainViewImport from '../views/QuickBagREST';
import MainModelImport from '../models/QuickBagREST';
import MainCollection from '../collections/QuickBagCollection';
import qbActions from './QuickBagActions';


const pageApp = PageApp.default || PageApp;
const MainView = MainViewImport.default || MainViewImport;
const MainModel = MainModelImport.default || MainModelImport;

const appChannel = pageApp.channel ? pageApp.channel : pageApp.default.channel;
const qbHeaderSelector = '#quickBagHeader';
const myBagLinkSelector = '#bag-icon-container';
const itemCountSelector = '#itemCount';
const bagCountSelector = '#bagCount';
const qbHeaderClass = 'quickBagFlyoutHeader';
const compositeViewElId = 'qbFlyout';
const compositeViewElClass = 'quickBagFlyout';
const showClass = 'hover';
const donotShowQuickBagClass = 'noQuickBag';
let qbv;
let $qbHeader;
let init;


// ###Method - validQuickBagUrl()
// Private Method to identify if on current page quickbag is shown or not. It other pages it will add "noQuickBag" class to body tag
//
// > parameters
// + *options* - This is an optional parameter. Primarily used for unit test to pass a dummy window object
//
// > returns
// >
// + *Boolean* - True if valid URL else false
//
function validQuickBagUrl(options) {
  const urls = ['/my-bag', '/my/bag', '/bag/atbpage', '/bag/merge', '/bag/checkout/merge'];
  // this is to ensure we don"t mess with original window object in unit test
  const winObj = (options && options.window) ? options.window : window;
  const currentUrl = winObj.location.href;
  const matchedURLs = _.filter(urls, url => currentUrl.indexOf(url) !== -1);

  if (matchedURLs.length) {
    $('body').addClass(donotShowQuickBagClass);
    if (matchedURLs.includes('/my-bag') || matchedURLs.includes('/my/bag')) {
      $('#qbFlyout').addClass('hide');
    }
  }

  return !matchedURLs.length;
}

// will open the quick bag after ensuring that bag items are updated
// promise will resolve with a data and a boolean true if fetched from server or false if fetched from cache
// promise will fail if fetching failed or quick is not allowed on the page
// argument options only being used for unit testing
function open(options) {
  // ensure the qbv is initialized. if it is already initialized it will not re-initialize
  init(options);

  // instead of checking for message from init, we just check if qbv is defined.
  // if it is undefined after calling init it is safe to assume that on this bag quickbag is not allowed
  /* istanbul ignore else */
  if (qbv) {
    return qbv.model.getData().then((obj) => {
      $qbHeader.addClass(showClass);
      $(`#${compositeViewElId}`).attr('aria-hidden', 'false');
      return obj;
    }, () => {
      $qbHeader.addClass(showClass);
      $(`#${compositeViewElId}`).attr('aria-hidden', 'false');
      return 'Update failed';
    });
  }
  return $.Deferred().reject('QuickBag not allowed');
}

// will return a promise
// promise will resolve with a data and a boolean true if fetched from server or false if fetched from cache
// promise will fail if fetching failed or quick is not allowed on the page
// argument options only being used for unit testing
function getData() {
  return MainModel.getData().then(obj => obj, () => 'Update failed');
}

function updateData() {
  MainModel.expireCache();
  return MainModel.getData().then(obj => obj, () => 'Update failed');
}

function updateItemCount(newCount) {
  /* istanbul ignore else */
  if (typeof newCount !== 'undefined') {
    if (qbv) {
      appChannel.trigger('header:setBagCount', newCount);
    } else {
      // Update text in element for Mobile - Quick Bag init event is not triggered for this mode.
      $(bagCountSelector).html(`${newCount}`);
      // should remove these spans. keeping it because globalCart.js (setHeader method) from NavApp is inserting these spans
      $(itemCountSelector).html(`(${newCount})`);
    }
  }
}

// this is currently being used by unit test only
/* eslint-disable no-unused-vars */
function destroy() {
  $('#qbFlyout').remove();
  $('#quickBagHeader').removeClass(qbHeaderClass);
  $('#openQbAccessibile').remove();
  qbv = undefined;
  $(document).off('.quickbag');
}

function addAccessibilityMarkup() {
  const accessibileBtn = "<button class='hide-for-screenreader' tabindex='0' id='openQbAccessibile'>Opens QuickBag flyout</button>";
  $(accessibileBtn).prependTo(qbHeaderSelector);
  $(document).on('click.quickbag', '#openQbAccessibile', () => {
    open();
  });
  $qbHeader.attr('aria-haspopup', 'true');
  $(`#${compositeViewElId}`).attr('aria-hidden', 'true');
}

function close() {
  /* istanbul ignore else */
  if (qbv) {
    $qbHeader.removeClass(showClass);
    $(`#${compositeViewElId}`).attr('aria-hidden', 'true');
  }
}

// ###Method - init( options )
// Private Method to add required DOM elements and initialize quickBagFlyout composite view
//
// > parameters
// + *options* - This is an optional parameter. Primarily used for unit test to pass a dummy window object
//
// > returns
// >
// + nothing || message: these messages will be passed back to quickbag:initialize callback
init = (options) => {
  if (qbv) {
    qbv.isHoverTagTriggered = false;
    /* Due to Adaptive View's ability to rerender its multiple views,
               *   the quick-bag view object should be destroyed before being recreated
               *   to prevent memory leaks
               */
    if (typeof qbv === 'object' && typeof qbv.destroy === 'function') {
      qbv.destroy();
    }
    qbv = null;
  }
  if (!qbv) {
    $qbHeader = $(qbHeaderSelector);
    $qbHeader.addClass(qbHeaderClass);

    const headerHideQuickBag = !!Globals.getValue('headerHideQuickBag');

    if (headerHideQuickBag) {
      $('#bagContainer').addClass('openOnClick');
    }

    if (validQuickBagUrl(options) && !headerHideQuickBag) {
      // we are adding this DIV and using it as composite view"s element. we are doing this because if we move
      // quickBagHeader as part of composite view"s element then every time we render this view, quickBagHeader will
      // render and it will trigger "mouseenter" event again causing to fire coremeterics tags twice.
      $qbHeader.append(`<div id=${compositeViewElId} class=${compositeViewElClass}></div>`);
      qbv = new MainView({
        el: `#${compositeViewElId}`,
        collection: new MainCollection(),
        model: new MainModel(),
        viewType: ($('header[data-uri]') && $('header[data-uri]').data() && $('header[data-uri]').data().context ? $('header[data-uri]').data().context.viewType : 'Default'),
      });
      qbv.isHoverTagTriggered = false;
      // This is required to prevent banner overlapping on quickbag dropdown Bug D-13499.
      // copied from earlier quickBag
      $('.widget .mainContainer .flexShadow').css('z-index', '0');

      addAccessibilityMarkup();
      qbv.model.getData();

      // do the mouseenter binding to ensure when user is shown updated list in the quickbag
      $(document).on('mouseenter.quickbag', myBagLinkSelector, () => {
        const qVStartTime = window.performance.now();
        Globals.setValue('qVStartTime', qVStartTime);
        qbv.model.getData().then((response) => {
          const metaisTaggingOnQuickBagEnabled = ObjectUtil.getChildProperty(response || {}, 'data.meta.isTaggingOnQuickBagEnabled');
          if (metaisTaggingOnQuickBagEnabled && !qbv.isHoverTagTriggered) {
            TagManagerUtil.fireTag('link', { event_name: 'minibag hover' });
            qbv.isHoverTagTriggered = true;
          }
        });
      }).on('keyup.quickbag', qbHeaderSelector, (ev) => {
        if (ev.which === 27) {
          close();
          $(myBagLinkSelector).focus();
        }
      });
    } else {
      return 'QuickBag not allowed';
    }
  }
  return 'QuickBag already initialized';
};


/*
 * Channel Events
 */
appChannel.on(qbActions.OPEN, (options) => {
  const callback = (options && options.callback) || options;
  open(options).always((obj) => {
    /* istanbul ignore else */
    if (callback && typeof callback === 'function') {
      callback(obj);
    }
  });
});

appChannel.on(qbActions.FORCE_UPDATE, (options) => {
  const callback = (options && options.callback) || options;
  updateData(options).always((obj) => {
    /* istanbul ignore else */
    if (callback && typeof callback === 'function') {
      callback(obj);
    }
  });
});

appChannel.on(qbActions.CLOSE, (options) => {
  close();
});

appChannel.on(qbActions.GET_DATA, (options) => {
  const callback = (options && options.callback) || options;
  getData(options).always((obj) => {
    /* istanbul ignore else */
    if (callback && typeof callback === 'function') {
      callback(obj);
    }
  });
});

appChannel.on(qbActions.UPDATE_COUNT, (newCount) => {
  updateItemCount(newCount);
});

appChannel.on(qbActions.INIT, (options) => {
  const callback = (options && options.callback) || options;
  const msg = init(options);
  /* istanbul ignore else */
  if (callback && typeof callback === 'function') {
    callback(msg);
  }
});

// TEMPORARY: add jquery event for PDP bag count
/* istanbul ignore next */
if (window && window.$ && window.$(document) && window.$(document).on) {
  window.$(document).on(qbActions.UPDATE_COUNT, (event, newCount) => {
    appChannel.trigger('header:setBagCount', newCount);
  });
}


/*
 * Export the functions unit test the functionality.
 */
export default {
  validQuickBagUrl,
  open,
  getData,
  updateData,
  updateItemCount,
  destroy,
  addAccessibilityMarkup,
  close,
  init,
  mockQbv: (mock) => { qbv = mock; },
  realQbv: (real) => { qbv = real; },
};
